import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { QueryStatus } from '@tanstack/react-query';
import { LENS_LAYOUT, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import AddIcon from '@mui/icons-material/Add';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import { Box, ButtonBase, IconButton, InputBase, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { AskRichardWordmark, BRAND_COLORS, LensMark } from '~/components/brand';

import RichardMonogram from '~/pages/AskRichardUnified/chat/RichardMonogram';
import {
  deriveSessionRowTitle,
  filterSessions,
  formatCompactRelativeTime,
} from '~/pages/AskRichardUnified/chat/SessionHistoryPanel';
import { RICHARD_MICROCOPY } from '~/pages/AskRichardUnified/chat/microcopy';

import { usePinReportingLeDataSession } from '~/hooks/apis/reporting/usePinReportingLeDataSession';
import { useRecentReportingLeDataSessions } from '~/hooks/apis/reporting/useRecentReportingLeDataSessions';
import useCurrentUser from '~/hooks/useCurrentUser';

import { formatDateWithClock } from '~/services/TimeService';

import type { Lens, LensSpace } from '~/types/leLens';

import { RailBetaBadge } from '../LeLens/BetaBadge';
import { AREA_CONTRACTS, AREA_IDS, type AreaId } from '../LeLens/areas/areaContract';
import { trackLensNav } from '../LeLens/areas/lensNavTelemetry';
import {
  ACCOMMODATION_NAVIGATION_ITEMS,
  type AccommodationModelId,
  COMMERCIAL_NAVIGATION_GROUPS,
  type CommercialAnalysisId,
} from '../LeLens/catalogue/commercial/navigation';
import type { GrowthPageId, GrowthSourceId } from '../LeLens/catalogue/growth/types';
import { type LensLibraryDestination, decodeLensLibraryDestination, lensesForDestination } from '../LeLens/lensLibrary';
import { rankTeams } from '../LeLens/library/teamRanking';

import { lensReadFailureText } from './lensReadFailure';
import { changedLensIds } from './lensVisits';
import {
  GROWTH_RAIL_ROWS,
  accommodationModelsShown,
  accommodationRowState,
  areaRowState,
  compactRecentSessions,
  keepActiveAtLimit,
} from './railContent';
import {
  THREAD_PINS_CHANGED_EVENT,
  THREAD_PIN_PREFERENCES_STORAGE_KEY,
  readThreadPinPreferences,
  saveThreadPinPreferences,
} from './threadPins';
import { useLensPinToggle } from './useLensPinToggle';
import { useLensVisits } from './useLensVisits';
import type { ViewKey } from './viewState';

interface RailProps {
  /** Null on contextual pages outside the analytics workspace. */
  view: ViewKey | null;
  /** The area page, or its full analysis in the catalogue, open. */
  activeAreaId?: AreaId | null;
  activeCommercialAnalysisId?: CommercialAnalysisId;
  /** The Growth rail row the address shows (see growthRailRowId). */
  activeGrowthRowId?: string | null;
  activeAccommodationModelId?: AccommodationModelId | null;
  /** Collapsed, the rail is a 60 px icon strip so the board keeps the room when a pane is open. */
  collapsed?: boolean;
  /** Flips collapsed. Absent, the rail shows no toggle. */
  onToggleCollapsed?: () => void;
  // The real askRichard session id open in the embedded chat, or null before any thread has been
  // opened. Drives both the highlighted rail row and the chat via the seam in LeDataChat's
  // controlledSessionId prop (see index.tsx).
  activeThreadId: string | null;
  activeLensId: string | null;
  onNavigate: (v: ViewKey, id?: string) => void;
  onOpenLpcOfferHealth?: () => void;
  onOpenArea: (areaId: AreaId) => void;
  onOpenCommercialAnalysis?: (analysisId: CommercialAnalysisId) => void;
  onOpenGrowthPage?: (pageId: GrowthPageId, source?: GrowthSourceId) => void;
  onOpenGrowthChannel?: (channel: string) => void;
  onOpenAccommodationModel?: (model: AccommodationModelId) => void;
  /** Opens the governed-data library without making it the Analytics landing page. */
  onOpenDataCatalogue?: () => void;
  /** Keeps Home and Data catalogue from both appearing selected when the library is open. */
  dataCatalogueActive?: boolean;
  onNewThread: () => void;
  /** Opens the New Lens sheet directly (the composer-first create conversation), with no brief yet. */
  onNewLens: () => void;
  // Real Lenses fetched from svc-reporting. Empty until the list call resolves, whether that ends
  // in success (an empty workspace) or failure (nothing reachable).
  realLenses: Array<Lens>;
  // The raw query status, not collapsed to a boolean: "still loading" and "unreachable" need
  // different rail treatments, a skeleton versus a stated failure with a retry, and collapsing
  // them made an ordinary first load claim the service was unreachable.
  realLensesStatus: QueryStatus;
  onRetryLenses?: () => void;
  /**
   * The failure itself, not just that there was one. A signed-out session and
   * an unreachable service both land here, and they need different things from
   * the reader.
   */
  realLensesError?: unknown;
  folders?: Array<LensSpace>;
  /** Shows the Certification row in the Data section. Reviewers only. */
  canReviewCertification?: boolean;
  certificationRequestCount?: number;
  onOpenLibrary: (destination: LensLibraryDestination) => void;
}

interface RailRowProps {
  icon: React.ReactNode;
  iconClassName?: string;
  label: string;
  labelTooltip?: string;
  trailing?: React.ReactNode;
  active: boolean;
  /** A parent of the current page: bold, no fill, and not announced as the current page. */
  open?: boolean;
  onClick: () => void;
  inset?: boolean;
  quiet?: boolean;
  dense?: boolean;
  indent?: number;
}

function RailRow({
  icon,
  iconClassName,
  label,
  labelTooltip,
  trailing,
  active,
  open = false,
  onClick,
  inset = false,
  quiet = false,
  dense = false,
  indent,
}: RailRowProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      aria-current={active ? 'page' : undefined}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: dense ? 0.75 : 1,
        pl: indent ?? (inset ? 2.25 : 1.25),
        pr: 1,
        minHeight: 32,
        py: dense ? 0.25 : inset ? 0.5 : 0.375,
        borderRadius: 1,
        textAlign: 'left',
        fontSize: dense ? '13px' : quiet ? '12.5px' : '13.5px',
        lineHeight: 1.25,
        fontWeight: active || open ? 600 : 400,
        color: active || open ? tokens.railText : quiet ? tokens.railTextMuted : tokens.railText,
        backgroundColor: active ? (inset ? tokens.railHover : tokens.railActive) : 'transparent',
        boxShadow: 'none',
        transition: 'background-color 120ms ease, color 120ms ease',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        '&:hover': { backgroundColor: active && !inset ? tokens.railActive : tokens.railHover, color: tokens.railText },
        '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '-2px' },
      }}
    >
      <Box
        className={iconClassName}
        sx={{
          display: 'flex',
          width: 18,
          justifyContent: 'center',
          opacity: 0.85,
          flex: 'none',
          transition: 'opacity 120ms ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        {icon}
      </Box>
      <Tooltip disableInteractive placement="right" title={labelTooltip ?? ''}>
        <Box
          component="span"
          sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {label}
        </Box>
      </Tooltip>
      {trailing}
    </ButtonBase>
  );
}

function RailSectionLabel({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        minHeight: 18,
        mb: '6px',
        mt: '18px',
        px: 1.25,
      }}
    >
      <Typography
        component="h3"
        sx={{
          color: alpha(tokens.railText, 0.56),
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.1em',
          lineHeight: 1.2,
          textTransform: 'uppercase',
        }}
      >
        {children}
      </Typography>
      {action ? <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.25, mr: -0.5 }}>{action}</Box> : null}
    </Box>
  );
}

function RailCount({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  return (
    <Box
      aria-hidden
      component="span"
      sx={{
        color: alpha(tokens.railText, 0.56),
        flex: 'none',
        fontSize: '11px',
        fontVariantNumeric: 'tabular-nums',
        mr: 0.5,
      }}
    >
      {children}
    </Box>
  );
}

const CHILD_ROW_INDENT = 4.25;

function RailChildRow({
  active,
  bullet = true,
  open = false,
  indent = CHILD_ROW_INDENT,
  label,
  onClick,
  trailing,
}: {
  active: boolean;
  /** Without a bullet the icon slot stays empty, for a disclosure button drawn over it. */
  bullet?: boolean;
  open?: boolean;
  indent?: number;
  label: string;
  onClick: () => void;
  trailing?: React.ReactNode;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <RailRow
      dense
      inset
      quiet
      active={active}
      open={open}
      icon={
        bullet ? (
          <Box
            sx={{
              bgcolor: active || open ? tokens.coralAccent : tokens.railTextMuted,
              borderRadius: '50%',
              height: 4,
              width: 4,
            }}
          />
        ) : null
      }
      indent={indent}
      label={label}
      onClick={onClick}
      trailing={trailing}
    />
  );
}

// A skeleton shaped like the eventual rows, never a bare spinner or placeholder text: the rail's
// own version of the design spec's loading-state rule.
function RailRowSkeleton({ count, indent, dense = false }: { count: number; indent?: number; dense?: boolean }) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <Stack spacing={dense ? 0 : '4px'} sx={{ pl: indent ?? 1.25, pr: 1.25 }}>
      {Array.from({ length: count }, (_, index) => (
        <Skeleton
          key={index}
          variant="rounded"
          // A dense row is 32px tall, so the reserved rows take the same space as the pins they stand for.
          height={dense ? 28 : 26}
          sx={{ borderRadius: 0.75, bgcolor: tokens.railHover, ...(dense ? { my: '2px' } : {}) }}
        />
      ))}
    </Stack>
  );
}

// A considered empty or error row for a nav section: an icon plus a human sentence, never bare
// text sitting alone in the gap where rows would be.
function RailInlineState({
  icon,
  text,
  onRetry,
  indent,
}: {
  icon: React.ReactNode;
  text: string;
  onRetry?: () => void;
  indent?: number;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <Box sx={{ display: 'flex', gap: 0.875, alignItems: 'flex-start', pl: indent ?? 1.25, pr: 1.25, py: 0.5 }}>
      <Box sx={{ display: 'flex', flex: 'none', mt: '1px', color: tokens.railTextMuted }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '11.5px', lineHeight: 1.5, color: tokens.railTextMuted }}>{text}</Typography>
        {onRetry ? (
          <ButtonBase
            onClick={onRetry}
            sx={{
              color: tokens.railText,
              fontSize: '11px',
              fontWeight: 600,
              mt: 0.25,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Retry
          </ButtonBase>
        ) : null}
      </Box>
    </Box>
  );
}

/** Matches shown for a query, enough to find one without becoming the whole list. */
const RAIL_SEARCH_RESULTS = 12;
/** Recents are shortcuts into work, not a second archive inside the rail. */
const RAIL_THREAD_PAGE_SIZE = 3;

const AREA_ICONS: Record<AreaId, React.ReactNode> = {
  commercial: <InsightsOutlinedIcon sx={{ fontSize: 16 }} />,
  growth: <TrendingUpOutlinedIcon sx={{ fontSize: 16 }} />,
  loyalty: <CardGiftcardOutlinedIcon sx={{ fontSize: 16 }} />,
  supply: <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />,
  'customer-service': <SupportAgentOutlinedIcon sx={{ fontSize: 16 }} />,
};

function RailSearchField({
  label,
  onChange,
  onClose,
  query,
}: {
  label: string;
  onChange: (query: string) => void;
  onClose: () => void;
  query: string;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <Box sx={{ mb: 0.5, px: 1.25 }}>
      <InputBase
        autoFocus
        endAdornment={
          <IconButton
            aria-label={`Close ${label.toLocaleLowerCase()}`}
            onClick={onClose}
            size="small"
            sx={{ color: 'inherit', p: 0.25, '&:hover': { backgroundColor: tokens.railHover } }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        }
        inputProps={{ 'aria-label': label }}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            onClose();
          }
        }}
        placeholder={label}
        startAdornment={<SearchIcon sx={{ color: 'inherit', fontSize: 15, mr: 0.75, opacity: 0.7 }} />}
        sx={{
          backgroundColor: 'transparent',
          border: `1px solid ${tokens.railBorder}`,
          borderRadius: 1.25,
          color: 'inherit',
          fontSize: '11.5px',
          minHeight: 32,
          px: 1,
          py: 0,
          width: '100%',
          transition: 'background-color 120ms ease, border-color 120ms ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          '& .MuiInputBase-input': {
            padding: 0,
          },
          '& input::placeholder': { color: tokens.railTextMuted, opacity: 1 },
          '&:hover': { backgroundColor: tokens.railHover },
          '&.Mui-focused': { backgroundColor: tokens.railHover, borderColor: tokens.railTextMuted },
        }}
        value={query}
      />
    </Box>
  );
}

function RailLabelButton({
  children,
  label,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);

  return (
    <IconButton
      aria-label={label}
      onClick={onClick}
      size="small"
      sx={{
        color: tokens.railTextMuted,
        height: 24,
        width: 24,
        '&:hover': { backgroundColor: tokens.railHover, color: tokens.railText },
        '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '-2px' },
      }}
    >
      {children}
    </IconButton>
  );
}

export function Rail({
  view,
  activeAreaId = null,
  activeCommercialAnalysisId,
  activeGrowthRowId = null,
  activeAccommodationModelId,
  collapsed = false,
  onToggleCollapsed,
  activeThreadId,
  activeLensId,
  onNavigate,
  onOpenArea,
  onOpenCommercialAnalysis,
  onOpenLpcOfferHealth,
  onOpenGrowthPage,
  onOpenGrowthChannel,
  onOpenAccommodationModel,
  onOpenDataCatalogue,
  dataCatalogueActive = false,
  onNewThread,
  onNewLens,
  realLenses,
  realLensesError,
  realLensesStatus,
  folders = [],
  onRetryLenses,
  canReviewCertification = false,
  certificationRequestCount = 0,
  onOpenLibrary,
}: RailProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const { user } = useCurrentUser();
  const [railHovered, setRailHovered] = useState(false);
  const {
    data: threadSessions = [],
    isLoading: threadsLoading,
    isError: threadsError,
    error: threadsErrorValue,
    refetch: refetchThreads,
  } = useRecentReportingLeDataSessions();
  const [threadQuery, setThreadQuery] = useState('');
  const [lensQuery, setLensQuery] = useState('');
  const [threadSearchOpen, setThreadSearchOpen] = useState(false);
  const [lensSearchOpen, setLensSearchOpen] = useState(false);
  const [threadLimit, setThreadLimit] = useState(RAIL_THREAD_PAGE_SIZE);
  const commercialShown = (view === 'area' || view === 'catalogue') && activeAreaId === 'commercial';
  const [accommodationModelsOpen, setAccommodationModelsOpen] = useState(
    accommodationModelsShown(commercialShown, activeCommercialAnalysisId),
  );
  // Follows the page, so leaving Accommodation folds its models. A manual toggle holds until the page changes.
  useEffect(() => {
    setAccommodationModelsOpen(accommodationModelsShown(commercialShown, activeCommercialAnalysisId));
  }, [commercialShown, activeCommercialAnalysisId]);
  const trimmedThreadQuery = threadQuery.trim();
  const trimmedLensQuery = lensQuery.trim();
  const searchingThreads = trimmedThreadQuery.length > 0;
  const searchingLenses = trimmedLensQuery.length > 0;
  // Keep the compact age true while someone leaves the workspace open.
  const [recencyNow, setRecencyNow] = useState(() => Math.floor(Date.now() / 60_000) * 60_000);
  useEffect(() => {
    const timer = window.setInterval(() => setRecencyNow(Math.floor(Date.now() / 60_000) * 60_000), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  // A Lens pin is a personal working-set preference, rather than shared dashboard state. Keep it
  // in the browser so pinning never changes someone else's rail or needs a write to the reporting
  // service just to remember a shortcut.
  const { pinnedIds: pinnedLensIds, toggle: toggleLensPin } = useLensPinToggle(
    realLensesStatus === 'success' ? realLenses.map((lens) => lens.id) : undefined,
  );
  const [threadPinPreferences, setThreadPinPreferences] = useState(readThreadPinPreferences);
  const threadPinPreferencesRef = useRef(threadPinPreferences);
  threadPinPreferencesRef.current = threadPinPreferences;

  useEffect(() => {
    const syncThreadPinPreferences = () => {
      const next = readThreadPinPreferences();
      threadPinPreferencesRef.current = next;
      setThreadPinPreferences(next);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === THREAD_PIN_PREFERENCES_STORAGE_KEY || event.key === null) {
        syncThreadPinPreferences();
      }
    };

    window.addEventListener(THREAD_PINS_CHANGED_EVENT, syncThreadPinPreferences);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(THREAD_PINS_CHANGED_EVENT, syncThreadPinPreferences);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  // What this reader has already opened, and at which version. It answers two return-visit
  // questions the Lens list on its own cannot: which boards have moved on since they last looked,
  // and what a pinned board is called when the list itself did not load.
  const lensVisits = useLensVisits();
  const changedLenses = useMemo(() => changedLensIds(realLenses, lensVisits), [realLenses, lensVisits]);
  // The rail shows a handful of work at rest. Each list owns its search so the
  // query and results cannot be mistaken for the neighbouring content type.
  const threadIsPinned = useCallback(
    (session: { sessionId: string; pinnedAt?: string | null }) =>
      threadPinPreferences[session.sessionId] ?? Boolean(session.pinnedAt),
    [threadPinPreferences],
  );
  const { pinnedThreads, unpinnedThreads } = useMemo(() => {
    const pinned: typeof threadSessions = [];
    const unpinned: typeof threadSessions = [];
    threadSessions.forEach((session) => (threadIsPinned(session) ? pinned : unpinned).push(session));
    return { pinnedThreads: pinned, unpinnedThreads: unpinned };
  }, [threadIsPinned, threadSessions]);
  const recentThreads = useMemo(() => {
    if (trimmedThreadQuery) {
      return filterSessions(threadSessions, trimmedThreadQuery).slice(0, RAIL_SEARCH_RESULTS);
    }
    return compactRecentSessions(unpinnedThreads, activeThreadId, threadLimit);
  }, [activeThreadId, threadLimit, trimmedThreadQuery, threadSessions, unpinnedThreads]);
  const hiddenThreads = searchingThreads ? 0 : Math.max(0, unpinnedThreads.length - recentThreads.length);
  // Pinned conversations lead the list without becoming a separate section.
  // The pin icon carries the distinction, so the rail can stay compact and the
  // thread order remains stable: pinned first, then recent.
  const threadGroups = useMemo(() => {
    if (searchingThreads) {
      return recentThreads.length ? [{ label: undefined, sessions: recentThreads }] : [];
    }
    const sessions = [...pinnedThreads, ...recentThreads];
    return sessions.length ? [{ label: undefined, sessions }] : [];
  }, [pinnedThreads, recentThreads, searchingThreads]);
  const pinSession = usePinReportingLeDataSession();
  const updateThreadPinPreferences = (next: Record<string, boolean>) => {
    threadPinPreferencesRef.current = next;
    setThreadPinPreferences(next);
    saveThreadPinPreferences(next);
  };
  const pinnedLensIdSet = useMemo(() => new Set(pinnedLensIds), [pinnedLensIds]);
  const pinnedLensActive = view === 'lens' && activeLensId !== null && pinnedLensIdSet.has(activeLensId);
  // Pin order is the order they were pinned in. A pin whose Lens is not in the loaded list is
  // already pruned by the hook, so nothing here points at a Lens that cannot open.
  const pinnedLenses = useMemo(
    () =>
      pinnedLensIds
        .map((id) => realLenses.find((lens) => lens.id === id))
        .filter((lens): lens is Lens => Boolean(lens)),
    [pinnedLensIds, realLenses],
  );
  const matchingLenses = useMemo(() => {
    const needle = trimmedLensQuery.toLocaleLowerCase();
    return needle ? realLenses.filter((lens) => lens.title.toLocaleLowerCase().includes(needle)) : realLenses;
  }, [realLenses, trimmedLensQuery]);
  const visibleLenses = searchingLenses ? matchingLenses.slice(0, RAIL_SEARCH_RESULTS) : [];
  const libraryDestination =
    view === 'lensIndex' ? decodeLensLibraryDestination(new URLSearchParams(window.location.search)) : null;
  const visibleTeams = keepActiveAtLimit(
    rankTeams(folders, realLenses, lensVisits, user?.email),
    libraryDestination?.startsWith('space:') ? libraryDestination.slice('space:'.length) : null,
    3,
  );
  const lensCounts = useMemo(
    () => ({ mine: lensesForDestination(realLenses, 'mine', user?.email).length }),
    [realLenses, user?.email],
  );
  const showLensCounts = realLensesStatus === 'success';
  // Counted on the matches, not on what fits, so a heading never claims fewer
  // than the search found.
  const threadMatchCount = searchingThreads ? filterSessions(threadSessions, trimmedThreadQuery).length : 0;

  const railOpenLibrary = (destination: LensLibraryDestination, target: string) => {
    trackLensNav({ name: 'rail_click', target });
    onOpenLibrary(destination);
  };
  const railOpenDataCatalogue = () => {
    trackLensNav({ name: 'rail_click', target: 'data-catalogue' });
    onOpenDataCatalogue?.();
  };

  const railWidthTransition = {
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
      easing: theme.transitions.easing.sharp,
    }),
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  };
  const stripButtonSx = {
    borderRadius: 1,
    color: tokens.railText,
    height: 36,
    width: 36,
    '&:hover': { backgroundColor: tokens.railHover },
    '&[aria-current="page"]': { backgroundColor: tokens.railActive },
    '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '-2px' },
  };
  const expandToggle = onToggleCollapsed ? (
    <Tooltip placement="right" title="Expand navigation">
      <IconButton
        aria-expanded={false}
        aria-label="Expand navigation"
        onClick={onToggleCollapsed}
        size="small"
        sx={stripButtonSx}
      >
        <KeyboardDoubleArrowRightIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;
  const strip = (
    <Box
      aria-label="Workspace"
      component="nav"
      sx={{
        alignItems: 'center',
        backgroundColor: tokens.rail,
        boxSizing: 'border-box',
        color: tokens.railText,
        display: 'flex',
        flex: 'none',
        flexDirection: 'column',
        gap: 0.5,
        height: '100%',
        pb: 2,
        pt: 1,
        width: LENS_LAYOUT.railCollapsedWidth,
        ...railWidthTransition,
      }}
    >
      {expandToggle}
      <ButtonBase
        aria-label="askRichard home"
        onClick={() => onOpenLibrary('home')}
        sx={{
          borderRadius: 1,
          mb: 1,
          mt: 0.5,
          p: 0.5,
          '&:hover': { backgroundColor: tokens.railHover },
        }}
      >
        <RichardMonogram inverted sx={{ fontSize: 30 }} />
      </ButtonBase>
      <Tooltip placement="right" title="Ask a question">
        <IconButton
          aria-label="Ask a question"
          onClick={onNewThread}
          size="small"
          sx={{
            ...stripButtonSx,
            backgroundColor: tokens.coralAccent,
            color: BRAND_COLORS.jetBlack,
            mb: 1,
            '&:hover': { backgroundColor: tokens.coralAccent, filter: 'brightness(0.96)' },
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip placement="right" title="Threads">
        <IconButton
          aria-current={view === 'thread' ? 'page' : undefined}
          aria-label="Threads"
          onClick={() => (activeThreadId ? onNavigate('thread', activeThreadId) : onNewThread())}
          size="small"
          sx={stripButtonSx}
        >
          <ForumOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip placement="right" title="Lenses">
        <IconButton
          aria-current={view === 'lensIndex' || (view === 'lens' && !pinnedLensActive) ? 'page' : undefined}
          aria-label="Lenses"
          onClick={() => railOpenLibrary('home', 'home')}
          size="small"
          sx={stripButtonSx}
        >
          <LensMark inverted size={20} />
        </IconButton>
      </Tooltip>
      {realLensesStatus === 'success' &&
        pinnedLenses.map((lens) => (
          <Tooltip key={lens.id} placement="right" title={lens.title}>
            <IconButton
              aria-current={view === 'lens' && activeLensId === lens.id ? 'page' : undefined}
              aria-label={lens.title}
              onClick={() => onNavigate('lens', lens.id)}
              size="small"
              sx={stripButtonSx}
            >
              <Box
                aria-hidden
                sx={{
                  alignItems: 'center',
                  border: `1.5px solid ${tokens.railText}`,
                  borderRadius: '50%',
                  display: 'flex',
                  fontSize: 11,
                  fontWeight: 600,
                  height: 20,
                  justifyContent: 'center',
                  lineHeight: 1,
                  width: 20,
                }}
              >
                {lens.title.trim().charAt(0).toUpperCase()}
              </Box>
            </IconButton>
          </Tooltip>
        ))}
      <Tooltip placement="right" title="New Lens">
        <IconButton aria-label="New Lens" onClick={onNewLens} size="small" sx={stripButtonSx}>
          <Box
            sx={{
              alignItems: 'center',
              border: `1.5px solid ${tokens.railText}`,
              borderRadius: '50%',
              display: 'flex',
              height: 18,
              justifyContent: 'center',
              width: 18,
            }}
          >
            <AddIcon sx={{ fontSize: 13 }} />
          </Box>
        </IconButton>
      </Tooltip>
      <Box aria-hidden sx={{ bgcolor: tokens.railHover, height: '1px', my: 1, width: 24 }} />
      {AREA_IDS.map((id) => (
        <Tooltip key={id} placement="right" title={AREA_CONTRACTS[id].label}>
          <IconButton
            aria-current={(view === 'area' || view === 'catalogue') && activeAreaId === id ? 'page' : undefined}
            aria-label={AREA_CONTRACTS[id].label}
            onClick={() => {
              trackLensNav({ name: 'rail_click', target: `area:${id}` });
              onOpenArea(id);
            }}
            size="small"
            sx={stripButtonSx}
          >
            {AREA_ICONS[id]}
          </IconButton>
        </Tooltip>
      ))}
      <Box aria-hidden sx={{ bgcolor: tokens.railHover, height: '1px', my: 1, width: 24 }} />
      {onOpenDataCatalogue && (
        <Tooltip placement="right" title="Data catalogue">
          <IconButton
            aria-current={dataCatalogueActive ? 'page' : undefined}
            aria-label="Data catalogue"
            onClick={railOpenDataCatalogue}
            size="small"
            sx={stripButtonSx}
          >
            <DatasetOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ flex: 1 }} />
    </Box>
  );

  const fadeSx = (shown: boolean) => ({
    opacity: shown ? 1 : 0,
    pointerEvents: shown ? ('auto' as const) : ('none' as const),
    transition: 'opacity 120ms ease',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    '&:focus-visible': {
      opacity: 1,
      outline: `2px solid ${tokens.coralAccent}`,
      outlineOffset: '2px',
      pointerEvents: 'auto',
    },
  });
  // The collapse control takes no space of its own. It fades in beside the wordmark while the
  // pointer is anywhere in the rail, and stays visible while it has keyboard focus.
  const collapseButton = onToggleCollapsed ? (
    <Tooltip placement="right" title="Collapse navigation">
      <IconButton
        aria-expanded
        aria-label="Collapse navigation"
        onClick={onToggleCollapsed}
        size="small"
        sx={{
          color: tokens.railTextMuted,
          position: 'absolute',
          right: 6,
          '&:hover': { backgroundColor: tokens.railHover, color: tokens.railText },
          ...fadeSx(railHovered),
        }}
      >
        <KeyboardDoubleArrowLeftIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  const expandedRail = (
    <Box
      component="nav"
      aria-label="Workspace"
      onMouseEnter={onToggleCollapsed ? () => setRailHovered(true) : undefined}
      onMouseLeave={onToggleCollapsed ? () => setRailHovered(false) : undefined}
      sx={{
        width: LENS_LAYOUT.railWidth,
        height: '100%',
        ...railWidthTransition,
        flex: 'none',
        backgroundColor: tokens.rail,
        color: tokens.railText,
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        // The rail remains independently scrollable (wheel, trackpad and keyboard), but it
        // should not read as a permanent second browser scrollbar. Reveal a narrow cue only
        // while the reader is engaging with this navigation.
        scrollbarWidth: 'thin',
        scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'transparent', borderRadius: 999 },
        '&:hover, &:focus-within': {
          scrollbarColor: `${tokens.railTextMuted} transparent`,
          '&::-webkit-scrollbar-thumb': { backgroundColor: tokens.railTextMuted },
        },
        px: 2,
        pt: 0,
        pb: 3,
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: tokens.rail,
          borderBottom: `1px solid ${tokens.railBorder}`,
          display: 'flex',
          flex: 'none',
          minHeight: 68,
          position: 'sticky',
          top: 0,
          width: '100%',
          zIndex: 1,
        }}
      >
        <ButtonBase
          aria-label="askRichard home"
          onClick={() => onOpenLibrary('home')}
          sx={{
            alignItems: 'center',
            borderRadius: 0,
            display: 'flex',
            flex: 1,
            gap: 1,
            justifyContent: 'center',
            minHeight: 68,
            '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '-2px' },
          }}
        >
          <RichardMonogram inverted sx={{ flexShrink: 0, fontSize: 30 }} />
          <AskRichardWordmark inverted size={22} />
        </ButtonBase>
        {collapseButton}
      </Box>
      <ButtonBase
        aria-label="Ask a question"
        onClick={onNewThread}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          backgroundColor: tokens.coralAccent,
          color: BRAND_COLORS.jetBlack,
          fontWeight: 600,
          fontSize: '13px',
          borderRadius: 1.25,
          minHeight: 34,
          py: 0.625,
          px: 1.75,
          mx: 0.75,
          mt: 1.75,
          mb: 1.75,
          boxShadow: 'none',
          '&:hover': { filter: 'brightness(0.96)' },
          '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '2px' },
        }}
      >
        <AddIcon sx={{ fontSize: 15 }} />
        Ask a question
      </ButtonBase>

      <RailSectionLabel
        action={
          !threadSearchOpen ? (
            <RailLabelButton label="Search threads" onClick={() => setThreadSearchOpen(true)}>
              <SearchIcon sx={{ fontSize: 15 }} />
            </RailLabelButton>
          ) : undefined
        }
      >
        {searchingThreads ? `Threads (${threadMatchCount})` : 'Threads'}
      </RailSectionLabel>
      {threadSearchOpen && (
        <RailSearchField
          label="Search threads"
          onChange={setThreadQuery}
          onClose={() => {
            setThreadQuery('');
            setThreadSearchOpen(false);
          }}
          query={threadQuery}
        />
      )}
      {threadsLoading && <RailRowSkeleton count={3} />}
      {!threadsLoading && threadsError && (
        <RailInlineState
          icon={<ErrorOutlineIcon sx={{ fontSize: 15 }} />}
          text={threadsErrorValue instanceof Error ? threadsErrorValue.message : 'Failed to load recent sessions.'}
          onRetry={() => void refetchThreads()}
        />
      )}
      {!threadsLoading && !threadsError && searchingThreads && recentThreads.length === 0 && (
        <RailInlineState icon={<ChatBubbleOutlineIcon sx={{ fontSize: 15 }} />} text="No thread matches that." />
      )}
      {!threadsLoading && !threadsError && !searchingThreads && threadSessions.length === 0 && (
        <RailInlineState icon={<ForumOutlinedIcon sx={{ fontSize: 15 }} />} text={RICHARD_MICROCOPY.emptySidebar} />
      )}
      {!threadsLoading && !threadsError && threadGroups.length > 0 && (
        <Stack spacing={0}>
          {threadGroups.map((group) => (
            <React.Fragment key={group.label ?? 'matches'}>
              {group.sessions.map((session) => {
                const title = deriveSessionRowTitle(session);
                const age = formatCompactRelativeTime(session.updatedAt, recencyNow);
                const isPinned = threadIsPinned(session);

                return (
                  <Box
                    key={session.sessionId}
                    sx={{
                      position: 'relative',
                      '&:hover .lens-rail-pin': { opacity: 1 },
                      '&:focus-within .lens-rail-pin': { opacity: 1 },
                    }}
                  >
                    <RailRow
                      dense
                      icon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
                      label={title}
                      trailing={
                        age ? (
                          <Tooltip placement="right" title={`Updated ${formatDateWithClock(session.updatedAt)}`}>
                            <Typography
                              className="rail-thread-age"
                              component="span"
                              sx={{
                                color: tokens.railTextMuted,
                                flex: 'none',
                                fontSize: '11px',
                                lineHeight: 1,
                                mr: 2.5,
                                opacity: 0.9,
                                transition: 'opacity 120ms ease',
                              }}
                            >
                              {age}
                            </Typography>
                          </Tooltip>
                        ) : undefined
                      }
                      active={view === 'thread' && activeThreadId === session.sessionId}
                      onClick={() => onNavigate('thread', session.sessionId)}
                    />
                    {/* A sibling rather than a child: the row is itself a button,
                        and a button inside a button is neither valid nor clickable. */}
                    <IconButton
                      aria-label={isPinned ? `Unpin ${title}` : `Pin ${title}`}
                      className="lens-rail-pin"
                      disabled={pinSession.isPending}
                      onClick={() => {
                        const pinned = !isPinned;
                        const previousPreferences = threadPinPreferencesRef.current;
                        const nextPreferences = { ...previousPreferences, [session.sessionId]: pinned };
                        updateThreadPinPreferences(nextPreferences);
                        pinSession.mutate(
                          { pinned, sessionId: session.sessionId },
                          {
                            onError: () => {
                              // A newer local/tab update wins over the failed request we started.
                              if (threadPinPreferencesRef.current[session.sessionId] === pinned) {
                                updateThreadPinPreferences(previousPreferences);
                              }
                            },
                          },
                        );
                      }}
                      size="small"
                      sx={{
                        color: 'inherit',
                        // Visible at rest only once pinned; otherwise it waits for hover or
                        // keyboard focus on the row (the row's own hover/focus-within rule
                        // above brings it to full opacity).
                        opacity: isPinned ? 0.9 : 0,
                        transition: 'opacity 120ms ease',
                        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                        position: 'absolute',
                        right: 4,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        '&:focus-visible': { opacity: 1 },
                      }}
                    >
                      {isPinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />}
                    </IconButton>
                  </Box>
                );
              })}
            </React.Fragment>
          ))}
        </Stack>
      )}
      {!searchingThreads && hiddenThreads > 0 && (
        <RailRow
          dense
          quiet
          icon={null}
          label="View more"
          labelTooltip={`View ${Math.min(RAIL_THREAD_PAGE_SIZE, hiddenThreads)} older threads`}
          active={false}
          onClick={() => setThreadLimit((current) => current + RAIL_THREAD_PAGE_SIZE)}
        />
      )}
      <RailSectionLabel
        action={
          !lensSearchOpen ? (
            <>
              <RailLabelButton label="Search lenses" onClick={() => setLensSearchOpen(true)}>
                <SearchIcon sx={{ fontSize: 15 }} />
              </RailLabelButton>
              <RailLabelButton label="New Lens" onClick={onNewLens}>
                <AddIcon sx={{ fontSize: 16 }} />
              </RailLabelButton>
            </>
          ) : undefined
        }
      >
        {searchingLenses ? `Lenses (${matchingLenses.length})` : 'Lenses'}
      </RailSectionLabel>
      {lensSearchOpen && (
        <RailSearchField
          label="Search lenses"
          onChange={setLensQuery}
          onClose={() => {
            setLensQuery('');
            setLensSearchOpen(false);
          }}
          query={lensQuery}
        />
      )}
      {!searchingLenses && (
        <Stack spacing={0}>
          <RailRow
            icon={<LensMark inverted size={18} />}
            label="Home"
            active={libraryDestination === 'home'}
            onClick={() => railOpenLibrary('home', 'home')}
          />
          <RailRow
            icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
            label="My Lenses"
            trailing={showLensCounts ? <RailCount>{lensCounts.mine}</RailCount> : undefined}
            active={libraryDestination === 'mine'}
            onClick={() => railOpenLibrary('mine', 'mine')}
          />
          <RailRow
            icon={<GridViewOutlinedIcon sx={{ fontSize: 16 }} />}
            label="Library"
            active={libraryDestination === 'teams' || Boolean(libraryDestination?.startsWith('space:'))}
            onClick={() => railOpenLibrary('teams', 'library')}
          />
          {visibleTeams.map((space) => (
            <RailRow
              dense
              icon={<GroupsOutlinedIcon sx={{ fontSize: 14 }} />}
              indent={3.25}
              key={space.id}
              label={space.name}
              labelTooltip={space.name}
              active={libraryDestination === `space:${space.id}`}
              onClick={() => onOpenLibrary(`space:${space.id}`)}
            />
          ))}
          {realLensesStatus === 'pending' && pinnedLensIds.length > 0 && (
            <RailRowSkeleton count={pinnedLensIds.length} dense indent={2.25} />
          )}
          {realLensesStatus === 'success' &&
            pinnedLenses.map((lens) => (
              <Box
                key={lens.id}
                sx={{
                  position: 'relative',
                  '&:hover .lens-rail-pinned-unpin, &:focus-within .lens-rail-pinned-unpin': { opacity: 1 },
                }}
              >
                <RailRow
                  dense
                  icon={<LensMark inverted size={15} />}
                  indent={2.25}
                  label={lens.title}
                  labelTooltip={lens.title}
                  active={view === 'lens' && activeLensId === lens.id}
                  onClick={() => onNavigate('lens', lens.id)}
                  // Keeps the title clear of the unpin icon that overlays the row's right edge.
                  trailing={<Box component="span" sx={{ flex: 'none', width: 20 }} />}
                />
                <Tooltip placement="right" title="Unpin from rail">
                  <IconButton
                    aria-label={`Unpin ${lens.title}`}
                    className="lens-rail-pinned-unpin"
                    onClick={() => toggleLensPin(lens.id)}
                    size="small"
                    sx={{
                      color: 'inherit',
                      opacity: 0,
                      position: 'absolute',
                      right: 4,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      transition: 'opacity 120ms ease',
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                      '&:focus-visible': { opacity: 1 },
                    }}
                  >
                    <PushPinIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
        </Stack>
      )}
      <>
        {realLensesStatus === 'pending' && searchingLenses && <RailRowSkeleton count={3} />}
        {realLensesStatus === 'error' && (
          <RailInlineState
            icon={<ErrorOutlineIcon sx={{ fontSize: 15 }} />}
            text={lensReadFailureText(realLensesError)}
            onRetry={onRetryLenses}
          />
        )}
        {realLensesStatus === 'success' && searchingLenses && matchingLenses.length === 0 && (
          <RailInlineState icon={<LensMark inverted size={18} />} text="No Lens matches that." />
        )}
        {realLensesStatus === 'success' && visibleLenses.length > 0 && (
          <Stack spacing={0}>
            {visibleLenses.map((lens) => {
              const isPinned = pinnedLensIdSet.has(lens.id);

              return (
                <Box
                  key={lens.id}
                  sx={{
                    position: 'relative',
                    '&:hover .lens-rail-lens-pin, &:focus-within .lens-rail-lens-pin': { opacity: 1 },
                  }}
                >
                  <RailRow
                    dense
                    icon={<LensMark inverted size={15} />}
                    label={lens.title}
                    labelTooltip={lens.title}
                    active={view === 'lens' && activeLensId === lens.id}
                    onClick={() => onNavigate('lens', lens.id)}
                    trailing={
                      <Box component="span" sx={{ alignItems: 'center', display: 'flex', gap: 0.625, mr: 2.25 }}>
                        {changedLenses.has(lens.id) && (
                          <Tooltip placement="right" title="Edited since you last opened this Lens">
                            <Box
                              aria-label="Edited since your last visit"
                              component="span"
                              role="img"
                              sx={{
                                backgroundColor: tokens.coralAccent,
                                borderRadius: '50%',
                                flex: 'none',
                                height: 6,
                                width: 6,
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    }
                  />
                  <IconButton
                    aria-label={isPinned ? `Unpin ${lens.title}` : `Pin ${lens.title}`}
                    className="lens-rail-lens-pin"
                    onClick={() => toggleLensPin(lens.id)}
                    size="small"
                    sx={{
                      color: 'inherit',
                      // Same rule as the thread pin: visible at rest only once pinned,
                      // otherwise revealed by row hover or keyboard focus.
                      opacity: isPinned ? 0.9 : 0,
                      transition: 'opacity 120ms ease',
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                      position: 'absolute',
                      right: 4,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      '&:focus-visible': { opacity: 1 },
                    }}
                  >
                    {isPinned ? <PushPinIcon sx={{ fontSize: 14 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />}
                  </IconButton>
                </Box>
              );
            })}
          </Stack>
        )}
      </>
      <RailSectionLabel>Business Pulse</RailSectionLabel>
      <Stack spacing={0}>
        {AREA_IDS.map((id) => {
          const areaActive = (view === 'area' || view === 'catalogue') && activeAreaId === id;
          const areaState = areaRowState(id, areaActive, activeCommercialAnalysisId, activeGrowthRowId);

          return (
            <React.Fragment key={id}>
              <RailRow
                icon={AREA_ICONS[id]}
                label={AREA_CONTRACTS[id].label}
                active={areaState === 'current'}
                open={areaState === 'open'}
                onClick={() => {
                  trackLensNav({ name: 'rail_click', target: `area:${id}` });
                  onOpenArea(id);
                }}
              />
              {areaActive && id === 'commercial' && onOpenCommercialAnalysis && (
                <Stack spacing="1px" sx={{ pb: 0.25 }}>
                  {COMMERCIAL_NAVIGATION_GROUPS.flatMap((group) => group.items).map((item) => {
                    if (item.id !== 'product-accommodation') {
                      return (
                        <RailChildRow
                          active={activeCommercialAnalysisId === item.id}
                          key={item.id}
                          label={item.label}
                          onClick={() => onOpenCommercialAnalysis(item.id)}
                        />
                      );
                    }
                    const modelSelected = ACCOMMODATION_NAVIGATION_ITEMS.some(
                      ({ id: modelId }) => modelId === activeAccommodationModelId,
                    );
                    const accommodationState = accommodationRowState(
                      activeCommercialAnalysisId === item.id,
                      modelSelected,
                    );
                    return (
                      <React.Fragment key={item.id}>
                        {/* The disclosure is a sibling drawn over the empty icon slot: the row is itself a button. */}
                        <Box sx={{ position: 'relative' }}>
                          <RailChildRow
                            active={accommodationState === 'current'}
                            bullet={false}
                            open={accommodationState === 'open'}
                            label={item.label}
                            onClick={() => {
                              setAccommodationModelsOpen(true);
                              onOpenCommercialAnalysis(item.id);
                            }}
                          />
                          <IconButton
                            aria-controls={accommodationModelsOpen ? 'rail-accommodation-models' : undefined}
                            aria-expanded={accommodationModelsOpen}
                            aria-label={`${accommodationModelsOpen ? 'Collapse' : 'Expand'} Accommodation models`}
                            onClick={() => setAccommodationModelsOpen((current) => !current)}
                            size="small"
                            sx={{
                              color: tokens.railTextMuted,
                              height: 24,
                              // Centred on RailRow's 18px icon slot, which starts at the child indent.
                              left: `calc(${theme.spacing(CHILD_ROW_INDENT)} - 3px)`,
                              p: 0,
                              position: 'absolute',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: 24,
                              '&:hover': { color: tokens.railText },
                              '&:focus-visible': { outline: `2px solid ${tokens.coralAccent}`, outlineOffset: '-2px' },
                            }}
                          >
                            <KeyboardArrowDownIcon
                              sx={{ fontSize: 16, transform: accommodationModelsOpen ? 'none' : 'rotate(-90deg)' }}
                            />
                          </IconButton>
                        </Box>
                        {accommodationModelsOpen && onOpenAccommodationModel && (
                          <Box
                            id="rail-accommodation-models"
                            sx={{
                              display: 'grid',
                              gap: '4px',
                              gridTemplateColumns: `repeat(${ACCOMMODATION_NAVIGATION_ITEMS.length}, minmax(0, 1fr))`,
                              pb: '6px',
                              pl: `calc(${theme.spacing(CHILD_ROW_INDENT)} - 2px)`,
                              pr: 1,
                              pt: '2px',
                            }}
                          >
                            {ACCOMMODATION_NAVIGATION_ITEMS.map((model) => {
                              const current =
                                activeCommercialAnalysisId === item.id && activeAccommodationModelId === model.id;
                              return (
                                <ButtonBase
                                  aria-current={current ? 'page' : undefined}
                                  focusRipple
                                  key={model.id}
                                  onClick={() => onOpenAccommodationModel(model.id)}
                                  sx={{
                                    bgcolor: current ? tokens.railActive : tokens.railHover,
                                    borderRadius: 0.75,
                                    color: current ? tokens.railText : tokens.railTextMuted,
                                    fontSize: '12.5px',
                                    fontWeight: current ? 600 : 500,
                                    height: 30,
                                    minWidth: 0,
                                    px: 0.5,
                                    transition: 'background-color 120ms ease, color 120ms ease',
                                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                    '&:hover': { bgcolor: tokens.railActive, color: tokens.railText },
                                    '&:focus-visible': {
                                      outline: `2px solid ${tokens.coralAccent}`,
                                      outlineOffset: '-2px',
                                    },
                                  }}
                                >
                                  {model.label}
                                </ButtonBase>
                              );
                            })}
                          </Box>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {onOpenLpcOfferHealth && (
                    <RailChildRow active={false} label="LPC Offer Health" onClick={onOpenLpcOfferHealth} />
                  )}
                </Stack>
              )}
              {areaActive && id === 'growth' && onOpenGrowthPage && onOpenGrowthChannel && (
                <Stack spacing="1px" sx={{ pb: 0.25 }}>
                  {GROWTH_RAIL_ROWS.map((row) => (
                    <RailChildRow
                      active={activeGrowthRowId === row.id}
                      key={row.id}
                      label={row.label}
                      onClick={() => {
                        const { channel, page, source } = row;
                        if (channel) {
                          onOpenGrowthChannel(channel);
                        } else if (page) {
                          onOpenGrowthPage(page, source);
                        }
                      }}
                    />
                  ))}
                </Stack>
              )}
            </React.Fragment>
          );
        })}
      </Stack>
      <RailSectionLabel>Data</RailSectionLabel>
      <Stack spacing={0}>
        {onOpenDataCatalogue && (
          <RailRow
            dense
            icon={<DatasetOutlinedIcon sx={{ fontSize: 15 }} />}
            label="Data catalogue"
            trailing={<RailBetaBadge />}
            active={dataCatalogueActive}
            onClick={railOpenDataCatalogue}
          />
        )}
        <RailRow
          dense
          icon={<MenuBookOutlinedIcon sx={{ fontSize: 15 }} />}
          label="Using data"
          active={view === 'dataGuide'}
          onClick={() => {
            trackLensNav({ name: 'rail_click', target: 'using-data' });
            onNavigate('dataGuide');
          }}
        />
        {canReviewCertification && (
          <RailRow
            dense
            icon={<VerifiedOutlinedIcon sx={{ fontSize: 15 }} />}
            label="Certification"
            labelTooltip={
              certificationRequestCount > 0 ? `${certificationRequestCount} awaiting certification` : undefined
            }
            trailing={certificationRequestCount > 0 ? <RailCount>{certificationRequestCount}</RailCount> : undefined}
            active={view === 'certification'}
            onClick={() => {
              trackLensNav({ name: 'rail_click', target: 'certification' });
              onNavigate('certification');
            }}
          />
        )}
      </Stack>
    </Box>
  );

  return collapsed ? strip : expandedRail;
}

export default Rail;
